/**
 * React Query hooks for /api/v1/wireline-slickline/* (WL-1).
 *
 * EVERY HOOK RETURNS THE META: the well identifier label and the tenant units
 * come from it. EVERY KEY STARTS ['wirelineSlickline'] and every write
 * invalidates that one prefix (a line event moves the line, the register and
 * the overview at once). EVERY READ SPREADS LOAD_FAILURE_QUERY_OPTIONS, so a
 * refusal is not retried and reads as a refusal, never as an empty list.
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type {
  WlCatalog,
  WlCrew,
  WlCrewMember,
  WlCrewMemberWrite,
  WlCrewWrite,
  WlCustomerSettings,
  WlCustomerSettingsUpdate,
  WlEventResult,
  WlLine,
  WlLineDetail,
  WlLineEventWrite,
  WlLineState,
  WlLineWrite,
  WlMeta,
  WlResult,
  WlSettings,
  WlSettingsUpdate,
  WlToolString,
  WlToolStringItemWrite,
  WlToolStringWrite,
  WlUnit,
  WlUnitDetail,
  WlUnitWrite,
  WlWell,
  WlWellWrite,
} from '../types/wireline-slickline';

/** No zone known: a screen that needs one says so rather than guessing. */
const NO_ZONE: string | null = null;

/** Meta did not arrive: no zone, no country and no units, rather than guessed ones. */
const EMPTY_META: WlMeta = {
  unitSystem: null,
  unitSystemSource: null,
  units: null,
  currency: null,
  country: null,
  taxSystem: null,
  timeZone: NO_ZONE,
  today: null,
};

/** Split a body into payload and meta. `data: null` stays null — never the envelope. */
export function unwrapWl<T>(body: unknown): WlResult<T | null> {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return { data: null, meta: EMPTY_META };
  const b = body as { data?: unknown; meta?: Partial<WlMeta> };
  const data = 'data' in b && b.data !== undefined && b.data !== null ? (b.data as T) : null;
  return { data, meta: { ...EMPTY_META, ...(b.meta ?? {}) } };
}

/** A list read: a non-array payload becomes `[]` rather than reaching `.map`. */
export function unwrapWlList<T>(body: unknown): WlResult<T[]> {
  const { data, meta } = unwrapWl<unknown>(body);
  return { data: Array.isArray(data) ? (data as T[]) : [], meta };
}

export interface WlWellFilters {
  customerId?: string;
  fluidType?: string;
  provinceState?: string;
  q?: string;
}

export interface WlLineFilters {
  lineType?: string;
}

const clean = (p?: object) => {
  if (!p) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(p)) if (v) out[k] = String(v);
  return Object.keys(out).length ? out : undefined;
};

export const wlKeys = {
  all: ['wirelineSlickline'] as const,
  meta: () => ['wirelineSlickline', 'meta'] as const,
  settings: () => ['wirelineSlickline', 'settings'] as const,
  customerSettings: (id: string) => ['wirelineSlickline', 'customerSettings', id] as const,
  customers: () => ['wirelineSlickline', 'customers'] as const,
  users: () => ['wirelineSlickline', 'users'] as const,
  equipment: () => ['wirelineSlickline', 'equipment'] as const,
  wells: (p?: WlWellFilters) => ['wirelineSlickline', 'wells', clean(p) ?? {}] as const,
  well: (id: string) => ['wirelineSlickline', 'well', id] as const,
  units: () => ['wirelineSlickline', 'units'] as const,
  unit: (id: string) => ['wirelineSlickline', 'unit', id] as const,
  lines: (p?: WlLineFilters) => ['wirelineSlickline', 'lines', clean(p) ?? {}] as const,
  line: (id: string) => ['wirelineSlickline', 'line', id] as const,
  lineState: (id: string) => ['wirelineSlickline', 'lineState', id] as const,
  toolStrings: () => ['wirelineSlickline', 'toolStrings'] as const,
  toolString: (id: string) => ['wirelineSlickline', 'toolString', id] as const,
};

export const invalidateWl = (qc: QueryClient) => qc.invalidateQueries({ queryKey: wlKeys.all });

function useWlWrite<V, R>(fn: (v: V) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSuccess: () => invalidateWl(qc) });
}

/** The catalog (ticket modes, line types, event types, positions, owners, units) with the API's labels. */
export function useWlMeta() {
  return useQuery({
    queryKey: wlKeys.meta(),
    queryFn: async () => unwrapWl<WlCatalog>((await api.wirelineSlickline.meta()).data),
    staleTime: 5 * 60_000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlSettings() {
  return useQuery({
    queryKey: wlKeys.settings(),
    queryFn: async () => unwrapWl<WlSettings>((await api.wirelineSlickline.settings.get()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdateWlSettings = () =>
  useWlWrite(async (body: WlSettingsUpdate) => unwrapWl<WlSettings>((await api.wirelineSlickline.settings.update(body)).data));

export function useWlCustomerSettings(customerId: string | undefined) {
  return useQuery({
    queryKey: wlKeys.customerSettings(customerId ?? ''),
    queryFn: async () => unwrapWl<WlCustomerSettings>((await api.wirelineSlickline.customerSettings.get(customerId as string)).data),
    enabled: !!customerId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useUpdateWlCustomerSettings = () =>
  useWlWrite(async ({ customerId, body }: { customerId: string; body: WlCustomerSettingsUpdate }) =>
    unwrapWl<WlCustomerSettings>((await api.wirelineSlickline.customerSettings.update(customerId, body)).data));

/** The tenant's customers, for the well and customer-settings pickers. */
export function useWlCustomers(enabled = true) {
  return useQuery({
    queryKey: wlKeys.customers(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; companyName?: string | null; company_name?: string | null }>((await api.customers.list()).data)
        .map((c) => ({ id: c.id, name: c.name || c.companyName || c.company_name || '—' }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's people (not terminated, not customer logins), for the crew picker. */
export function useWlUsers(enabled = true) {
  return useQuery({
    queryKey: wlKeys.users(),
    queryFn: async () =>
      unwrapArray<{ id: string; firstName?: string; lastName?: string; fullName?: string; role?: string;
        first_name?: string; last_name?: string; employmentStatus?: string; employment_status?: string }>((await api.users.list()).data)
        .filter((u) => (u.employmentStatus ?? u.employment_status) !== 'terminated' && u.role !== 'customer')
        .map((u) => ({
          id: u.id,
          name: (u.fullName || [u.firstName ?? u.first_name, u.lastName ?? u.last_name].filter(Boolean).join(' ')).trim() || '—',
          role: u.role ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's owned equipment (not retired), for the unit, mast, drum and tool pickers. */
export function useWlEquipmentOptions(enabled = true) {
  return useQuery({
    queryKey: wlKeys.equipment(),
    queryFn: async () =>
      unwrapArray<{ id: string; name?: string | null; unitNumber?: string | null; unit_number?: string | null;
        serialNumber?: string | null; serial_number?: string | null; status?: string | null }>((await api.equipment.list()).data)
        .filter((e) => e.status !== 'retired')
        .map((e) => ({
          id: e.id,
          name: e.name || '—',
          unitNumber: e.unitNumber ?? e.unit_number ?? null,
          serialNumber: e.serialNumber ?? e.serial_number ?? null,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

// ─── Wells ───────────────────────────────────────────────────────────────────

export function useWlWells(filters?: WlWellFilters) {
  return useQuery({
    queryKey: wlKeys.wells(filters),
    queryFn: async () => unwrapWlList<WlWell>((await api.wirelineSlickline.wells.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlWell(id: string | undefined) {
  return useQuery({
    queryKey: wlKeys.well(id ?? ''),
    queryFn: async () => unwrapWl<WlWell>((await api.wirelineSlickline.wells.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWlWell = () =>
  useWlWrite(async (body: WlWellWrite) => unwrapWl<WlWell>((await api.wirelineSlickline.wells.create(body)).data));
export const useUpdateWlWell = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlWellWrite }) =>
    unwrapWl<WlWell>((await api.wirelineSlickline.wells.update(id, body)).data));
export const useDeleteWlWell = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.wells.remove(id)).data);

// ─── Units, crews, members ───────────────────────────────────────────────────

export function useWlUnits() {
  return useQuery({
    queryKey: wlKeys.units(),
    queryFn: async () => unwrapWlList<WlUnit>((await api.wirelineSlickline.units.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlUnit(id: string | undefined) {
  return useQuery({
    queryKey: wlKeys.unit(id ?? ''),
    queryFn: async () => unwrapWl<WlUnitDetail>((await api.wirelineSlickline.units.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWlUnit = () =>
  useWlWrite(async (body: WlUnitWrite) => unwrapWl<WlUnit>((await api.wirelineSlickline.units.create(body)).data));
export const useUpdateWlUnit = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlUnitWrite }) =>
    unwrapWl<WlUnit>((await api.wirelineSlickline.units.update(id, body)).data));
export const useDeleteWlUnit = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.units.remove(id)).data);

export const useCreateWlCrew = () =>
  useWlWrite(async ({ unitId, body }: { unitId: string; body: WlCrewWrite }) =>
    unwrapWl<WlCrew>((await api.wirelineSlickline.crews.create(unitId, body)).data));
export const useUpdateWlCrew = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlCrewWrite }) =>
    unwrapWl<WlCrew>((await api.wirelineSlickline.crews.update(id, body)).data));
export const useDeleteWlCrew = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.crews.remove(id)).data);

export const useAddWlCrewMember = () =>
  useWlWrite(async ({ crewId, body }: { crewId: string; body: WlCrewMemberWrite }) =>
    unwrapWl<WlCrewMember>((await api.wirelineSlickline.crewMembers.add(crewId, body)).data));
export const useUpdateWlCrewMember = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlCrewMemberWrite }) =>
    unwrapWl<WlCrewMember>((await api.wirelineSlickline.crewMembers.update(id, body)).data));
export const useRemoveWlCrewMember = () =>
  useWlWrite(async (id: string) => (await api.wirelineSlickline.crewMembers.remove(id)).data);

// ─── Lines ───────────────────────────────────────────────────────────────────

export function useWlLines(filters?: WlLineFilters) {
  return useQuery({
    queryKey: wlKeys.lines(filters),
    queryFn: async () => unwrapWlList<WlLine>((await api.wirelineSlickline.lines.list(clean(filters))).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlLine(id: string | undefined) {
  return useQuery({
    queryKey: wlKeys.line(id ?? ''),
    queryFn: async () => unwrapWl<WlLineDetail>((await api.wirelineSlickline.lines.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The folded state, from its own endpoint: the panel renders this, never a client fold. */
export function useWlLineState(id: string | undefined) {
  return useQuery({
    queryKey: wlKeys.lineState(id ?? ''),
    queryFn: async () => unwrapWl<WlLineState>((await api.wirelineSlickline.lines.state(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWlLine = () =>
  useWlWrite(async (body: WlLineWrite) => unwrapWl<WlLine>((await api.wirelineSlickline.lines.create(body)).data));
export const useUpdateWlLine = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlLineWrite }) =>
    unwrapWl<WlLine>((await api.wirelineSlickline.lines.update(id, body)).data));
export const useDeleteWlLine = () => useWlWrite(async (id: string) => (await api.wirelineSlickline.lines.remove(id)).data);

export const useAddWlLineEvent = () =>
  useWlWrite(async ({ lineId, body }: { lineId: string; body: WlLineEventWrite }) =>
    unwrapWl<WlEventResult>((await api.wirelineSlickline.lines.addEvent(lineId, body)).data));
export const useVoidWlLineEvent = () =>
  useWlWrite(async ({ eventId, reason }: { eventId: string; reason: string }) =>
    unwrapWl<WlEventResult>((await api.wirelineSlickline.lineEvents.void(eventId, { reason })).data));

// ─── Tool strings ────────────────────────────────────────────────────────────

export function useWlToolStrings() {
  return useQuery({
    queryKey: wlKeys.toolStrings(),
    queryFn: async () => unwrapWlList<WlToolString>((await api.wirelineSlickline.toolStrings.list()).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function useWlToolString(id: string | undefined) {
  return useQuery({
    queryKey: wlKeys.toolString(id ?? ''),
    queryFn: async () => unwrapWl<WlToolString>((await api.wirelineSlickline.toolStrings.get(id as string)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export const useCreateWlToolString = () =>
  useWlWrite(async (body: WlToolStringWrite) => unwrapWl<WlToolString>((await api.wirelineSlickline.toolStrings.create(body)).data));
export const useUpdateWlToolString = () =>
  useWlWrite(async ({ id, body }: { id: string; body: WlToolStringWrite }) =>
    unwrapWl<WlToolString>((await api.wirelineSlickline.toolStrings.update(id, body)).data));
export const useReplaceWlToolStringItems = () =>
  useWlWrite(async ({ id, items }: { id: string; items: WlToolStringItemWrite[] }) =>
    unwrapWl<WlToolString>((await api.wirelineSlickline.toolStrings.replaceItems(id, { items })).data));
export const useDeleteWlToolString = () =>
  useWlWrite(async (id: string) => (await api.wirelineSlickline.toolStrings.remove(id)).data);
